import React, {useEffect, useRef} from 'react';
import {Animated, StyleSheet, View, type StyleProp, type ViewStyle} from 'react-native';

import {radii, spacing, useTheme} from '@/shared/ui/theme';

/**
 * Loading placeholders in the SHAPE of the thing that is coming.
 *
 * A centred spinner tells the user only that something is happening; a skeleton tells
 * them what is about to appear and keeps the layout from jumping when it does. That
 * second part is the real win on a feed: with a spinner the list snaps into place and
 * the eye has to re-find its place, with skeletons the rows simply fill in.
 *
 * The pulse is opacity-only on the native driver — no layout, no colour interpolation,
 * so it costs nothing even with a dozen on screen.
 */
const PULSE_MS = 900;

const styles = StyleSheet.create({
  row: {flexDirection: 'row', alignItems: 'center', gap: spacing(3)},
  body: {flex: 1, gap: spacing(2)},
  list: {gap: spacing(2)},
  card: {
    borderRadius: radii.md,
    borderWidth: StyleSheet.hairlineWidth,
    padding: spacing(3),
  },
});

/** One shimmering block. */
export function Skeleton({
  width,
  height = 12,
  radius = radii.sm,
  style,
}: {
  width?: number | `${number}%`;
  height?: number;
  radius?: number;
  style?: StyleProp<ViewStyle>;
}) {
  const p = useTheme();
  const pulse = useRef(new Animated.Value(0.5)).current;

  useEffect(() => {
    const loop = Animated.loop(
      Animated.sequence([
        Animated.timing(pulse, {
          toValue: 1,
          duration: PULSE_MS,
          useNativeDriver: true,
        }),
        Animated.timing(pulse, {
          toValue: 0.5,
          duration: PULSE_MS,
          useNativeDriver: true,
        }),
      ]),
    );
    loop.start();
    return () => loop.stop();
  }, [pulse]);

  return (
    <Animated.View
      accessibilityElementsHidden
      importantForAccessibility="no-hide-descendants"
      style={[
        {
          width: width ?? '100%',
          height,
          borderRadius: radius,
          backgroundColor: p.skeleton,
          opacity: pulse,
        },
        style,
      ]}
    />
  );
}

/**
 * The list-row skeleton: a leading disc, two lines of unequal length (a name is longer
 * than what sits under it), and a short trailing block where the time or pill goes.
 */
export function SkeletonRow({avatar = true}: {avatar?: boolean}) {
  const p = useTheme();
  return (
    <View style={[styles.card, {backgroundColor: p.card, borderColor: p.border}]}>
      <View style={styles.row}>
        {avatar ? <Skeleton width={36} height={36} radius={18} /> : null}
        <View style={styles.body}>
          <Skeleton width="62%" height={12} />
          <Skeleton width="38%" height={10} />
        </View>
        <Skeleton width={52} height={20} radius={radii.xs} />
      </View>
    </View>
  );
}

/** A screenful of row skeletons — what a feed shows on its first load. */
export function SkeletonList({
  count = 6,
  avatar = true,
}: {
  count?: number;
  avatar?: boolean;
}) {
  return (
    <View style={styles.list} testID="skeleton-list">
      {Array.from({length: count}, (_, i) => (
        <SkeletonRow key={i} avatar={avatar} />
      ))}
    </View>
  );
}
